import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { useTranslation } from '@/src/i18n';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import InstructionItem from '@/components/atoms/InstructionItem/InstructionItem';
import { InstructionSection } from '@/components/utils/instruction';

type Props = {
  onSubmit: () => void;
  instructionSections: InstructionSection[];
  topElement?: ReactNode;
};

const StyledConsentForm = styled(Stack)(
  ({ theme }) => css`
    justify-content: space-between;
    height: 100%;

    .instructions {
      max-height: calc(100vh - ${theme.spacing(20)});

      ${theme.breakpoints.up('sm')} {
        max-height: calc(70vh - ${theme.spacing(20)});
        min-width: 600px;
      }

      /* ol here is the instructions container, see the Stack in ConsentForm component */
      ol {
        padding-bottom: ${theme.spacing(2)};
        align-items: center;
      }
    }
  `,
);

const ConsentForm = ({ onSubmit, instructionSections, topElement }: Props) => {
  const { t } = useTranslation();
  const [currentInstruction, setCurrentInstruction] = useState(1);
  const instructionsSection = useRef<null | HTMLDivElement>(null);

  const totalInstructions = instructionSections.reduce(
    (acc, cur) => acc + cur.instructionItems.length,
    0,
  );

  const submitBtnLabel = `${t(
    'accept-instructions',
  )} (${currentInstruction}/${totalInstructions})`;

  const onAcceptInstruction = () => {
    if (currentInstruction === totalInstructions) {
      analyticsEvent({
        category: 'Booking',
        action: 'ClickAcceptInstruction',
      });

      onSubmit();
    } else {
      setCurrentInstruction(prevState => prevState + 1);
    }
  };

  useEffect(() => {
    if (instructionsSection.current) {
      instructionsSection.current.lastElementChild?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [currentInstruction]);
  // index for total instruction in sections
  let instructionIndex = 0;
  return (
    <>
      <DialogContent>
        <StyledConsentForm>
          {topElement}
          <div className="instructions" ref={instructionsSection}>
            {instructionSections.map((instructionSection, sectionIndex) => {
              let previousItemsLength = 0;
              for (let i = 0; i < sectionIndex; i++) {
                previousItemsLength +=
                  instructionSections[i]?.instructionItems.length;
              }
              const isCurrentSection = currentInstruction > previousItemsLength;
              if (
                instructionSection.instructionItems.length > 0 &&
                isCurrentSection
              ) {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`section-${sectionIndex}`}>
                    {instructionSection.sectionTitle && (
                      <Typography className="popup-content-title">
                        {t(instructionSection.sectionTitle)}
                      </Typography>
                    )}
                    <Stack component="ol" spacing={2}>
                      {instructionSection.instructionItems.map(
                        (instruction, index) => {
                          if (
                            index + previousItemsLength <
                            currentInstruction
                          ) {
                            return (
                              <InstructionItem
                                key={instruction.id}
                                instruction={instruction}
                                isAccepted={
                                  instructionIndex++ < currentInstruction - 1
                                }
                              />
                            );
                          }
                          return null;
                        },
                      )}
                    </Stack>
                  </React.Fragment>
                );
              }
              return null;
            })}
          </div>
        </StyledConsentForm>
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          onClick={onAcceptInstruction}
          data-cy="btn-rdv-confirm-consents"
        >
          {submitBtnLabel}
        </Button>
      </DialogActions>
    </>
  );
};

export default ConsentForm;
